import React, { useState, useCallback } from 'react';
import { logger } from '../../log';
import styles from './CopyButton.module.css';

const log = logger('copy-button');

export interface CopyButtonProps {
  /**
   * The text to copy to clipboard
   */
  value: string;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * ARIA label for screen readers
   * @default 'Copy to clipboard'
   */
  'aria-label'?: string;
}

/**
 * CopyButton Component
 *
 * A minimal copy-to-clipboard button following Bauhaus design principles.
 * Shows a subtle copy icon that transitions to a checkmark on success.
 *
 * Design features:
 * - Minimal footprint for inline use
 * - No border radius (geometric purity)
 * - Subtle hover state
 * - Brief feedback animation on copy
 *
 * @example
 * ```tsx
 * <CopyButton value={projectId} />
 * <span className={styles.id}>{projectId}</span>
 * ```
 */
export const CopyButton: React.FC<CopyButtonProps> = ({
  value,
  className,
  'aria-label': ariaLabel = 'Copy to clipboard',
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      log.error('failed to copy', err);
    }
  }, [value]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`${styles.copyButton} ${copied ? styles.copied : ''} ${className || ''}`}
      aria-label={ariaLabel}
      title={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="square"
          strokeLinejoin="miter"
        >
          <rect x="9" y="9" width="13" height="13" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
};
